import { createSelector } from 'reselect';
import {
  at,
  constant,
  compact,
  first,
  flatMap,
  flatten,
  flow,
  get,
  getOr,
  identity,
  isNil,
  isObject,
  join,
  map,
  find,
  isEqual,
  omit,
  partition,
  reverse,
  set,
  slice,
  split,
  toLower,
  castArray,
} from 'lodash/fp';

import { inputBoxesToArray } from 'components/forms/InputBoxes';
import { RETAIL, PRIVATE_SALE, MERCHANT } from 'constants/sellerType';
import { AD_FIELDS } from 'constants/ad';
import { idsByValue as bodyTypeIdsByValue } from 'helpers/fieldOptions/car/bodyType';
import { decodeName } from 'helpers/fieldOptions';
import getMapper, { cleanEmptyValues, mapPath } from 'helpers/mapper';
import { VALUE_10 as DEFAULT_PAGE_SIZE } from 'helpers/fieldOptions/ad/pageSize';
import { GEOLOCATION_DISTANCE_ASC } from 'helpers/fieldOptions/ad/sort';
import getCountryByLocale from 'helpers/country/getCountryByLocale';
import {
  options as adSellerTypeOptions,
  EXCLUSIVE,
} from 'helpers/fieldOptions/ad/sellerType';

import {
  AIRBAG_NAME,
  BODY_TYPE_NAME,
  COLOR_NAME,
  COMFORT_NAME,
  CONSUMPTION_RANGE_NAME,
  DEFAULT_GEOLOCATION_DISTANCE,
  DOOR_RANGE_NAME,
  EMISSION_STICKER_NAME,
  ENV_STANDARD_NAME,
  EXTRAS_NAME,
  FIELDS_NAME,
  FUEL_TYPE_NAME,
  GEOLOCATION_NAME,
  HAS_IMAGES_NAME,
  INTERIOR_NAME,
  LIGHT_NAME,
  LOCALE_NAME,
  MAKE_NAME,
  METALLIC_NAME,
  MILEAGE_RANGE_NAME,
  MODEL_NAME,
  MONTHLY_PRICE_RANGE_NAME,
  MULTIMEDIA_NAME,
  NOT_DAMAGED_NAME,
  PAGE_NAME,
  PAGE_SIZE_NAME,
  PARKING_SENSORS_NAME,
  POWER_RANGE_NAME,
  PREV_OWNER_NAME,
  PRICE_RANGE_NAME,
  SEAT_COMFORT_NAME,
  SEAT_RANGE_NAME,
  SECURITY_NAME,
  SELLER_ID_NAME,
  SELLER_TYPE_NAME,
  SERVICE_HISTORY_NAME,
  SMOKER_NAME,
  SORT_NAME,
  SUB_TYPE_NAME,
  TRANSMISSION_NAME,
  UPHOLSTERY_NAME,
  VEHICLE_NAME,
  YEAR_RANGE_NAME,
} from '../../constants';

const toBoolean = (value) => {
  if (isNil(value)) {
    return;
  }

  return value || value === 'true';
};
const toNumber = (value) => {
  if (isNil(value) || value === '') {
    return;
  }

  return Number(value);
};
const isNumeric = (value) => /^-{0,1}\d+$/.test(value);

const moveKey =
  (keyFrom, keyTo, toType = identity) =>
  (object) => {
    const value = toType(get([keyFrom], object));

    object = omit([keyFrom], object);

    if (isNil(value)) {
      return object;
    }

    return set([keyTo], value, object);
  };

const moveRange = (value) => {
  if (isNil(value)) {
    return;
  }

  return isObject(value)
    ? flow(
        moveKey('min', 'from', toNumber),
        moveKey('max', 'to', toNumber),
      )(value)
    : {
        from: toNumber(value),
        to: toNumber(value),
      };
};

const getRange = (path) => flow(get(path), moveRange);

const getCheckboxList = (path) =>
  flow(get(path), inputBoxesToArray(decodeName));

const getCheckboxNumbers = (path) => flow(getCheckboxList(path), map(toNumber));

const getOuterColorType = flow(
  getCheckboxList([METALLIC_NAME]),
  get([0]),
  toBoolean,
);

const getBodyType = flow(
  getCheckboxList([BODY_TYPE_NAME]),
  flatMap((key) => get([decodeName(key)], bodyTypeIdsByValue)),
  map(toNumber),
);

const getLocale = flow(get([LOCALE_NAME]), toLower);

const getCountry = flow(getLocale, (locale) => getCountryByLocale(locale));

const getLocation = flow(
  (args) => [...at([GEOLOCATION_NAME])(args), getCountry(args)],
  ([geoLocation = {}, country]) => {
    const [lat, lng, distance] = at(['lat', 'lng', 'distance'], geoLocation);
    const hasLatLng = !!(lat && lng);

    const locations = hasLatLng
      ? [
          {
            lat: toNumber(lat),
            lon: toNumber(lng),
            distance: isNil(distance)
              ? DEFAULT_GEOLOCATION_DISTANCE
              : toNumber(distance),
          },
        ]
      : [];

    if (country) {
      return {
        countries: [country],
        locations,
      };
    }
  },
);

const defaultSort = [];

const getSort = flow(
  at([SORT_NAME, GEOLOCATION_NAME]),
  ([sortField, geoLocation]) => {
    if (sortField) {
      const direction = flow(split('.'), reverse, first)(sortField);

      if (sortField === GEOLOCATION_DISTANCE_ASC) {
        const { lat, lng: lon } = geoLocation;

        return [
          ...defaultSort,
          {
            distance: { lat, lon },
            direction,
          },
        ];
      }

      return [
        ...defaultSort,
        {
          field: flow(split('.'), slice(0, -1), join('.'))(sortField),
          direction,
        },
      ];
    }

    return defaultSort;
  },
);

const getNumberAsArray = (path) =>
  flow(get(path), (value) =>
    isNil(value) || value === '' ? [] : [toNumber(value)],
  );

const getFeatureAndEquipmentGroups = (type) =>
  createSelector(
    getNumberAsArray([AIRBAG_NAME]),
    getCheckboxList([LIGHT_NAME]),
    getCheckboxList([COMFORT_NAME]),
    getCheckboxList([SEAT_COMFORT_NAME]),
    getCheckboxList([MULTIMEDIA_NAME]),
    getCheckboxList([INTERIOR_NAME]),
    getCheckboxList([EXTRAS_NAME]),
    getCheckboxList([PARKING_SENSORS_NAME]),
    getCheckboxList([SECURITY_NAME]),
    (...args) =>
      flow(
        flatten,
        partition((item) => isNumeric(item)),
        (arr) => (type === 'equipments' ? flow(map(toNumber))(arr[0]) : arr[1]),
      )(args),
  );

const getMakes = flow(
  get([VEHICLE_NAME, '0']),
  at([MAKE_NAME, MODEL_NAME, SUB_TYPE_NAME]),
  ([make, model, subType]) => {
    if (!make) {
      return;
    }

    let makeModel = { make };

    if (model) {
      makeModel = {
        ...makeModel,
        models: [model],
      };

      if (subType) {
        makeModel = {
          ...makeModel,
          modelsWithSubTypes: [{ model, subTypes: [subType] }],
        };
      }
    }

    return [makeModel];
  },
);

const getFromProperty = createSelector(
  get([PAGE_SIZE_NAME]),
  get([PAGE_NAME]),
  (pageSize = DEFAULT_PAGE_SIZE, page = 1) => (page - 1) * pageSize,
);

const getExclusive = flow(
  get([SELLER_TYPE_NAME]),
  (value) =>
    find((option) => isEqual(option.value, value))(adSellerTypeOptions),
  get(['provider']),
  (provider) => {
    if (isNil(provider)) {
      return;
    }

    return provider === EXCLUSIVE;
  },
);

export const scaffolding = {
  sellerType: [RETAIL, PRIVATE_SALE, MERCHANT],
};

const getDamagedBool = flow(get([NOT_DAMAGED_NAME]), (value) => {
  if (value === true) {
    return false;
  }
});

export default getMapper(
  [
    mapPath(['bodyType'], getBodyType),
    mapPath(['carPreownerCount'], getRange([PREV_OWNER_NAME])),
    mapPath(['fuelConsumptionCombined'], getRange([CONSUMPTION_RANGE_NAME])),
    mapPath(['doorCount'], getRange([DOOR_RANGE_NAME])),
    mapPath(
      ['emissionSticker'],
      flow(get([EMISSION_STICKER_NAME]), castArray, compact),
    ),
    mapPath(
      ['environmentalStandard'],
      flow(get([ENV_STANDARD_NAME]), castArray, compact),
    ),
    mapPath(['equipmentGroups'], getFeatureAndEquipmentGroups('equipments')),
    mapPath(['features'], getFeatureAndEquipmentGroups('features')),
    mapPath(['from'], getFromProperty),
    mapPath(['fuelType'], getCheckboxNumbers([FUEL_TYPE_NAME])),
    mapPath(['gearType'], getCheckboxNumbers([TRANSMISSION_NAME])),
    mapPath(['horsePower'], getRange([POWER_RANGE_NAME])),
    mapPath(['locale'], getLocale),
    mapPath(['location'], getLocation),
    mapPath(['makes'], getMakes),
    mapPath(['mileage'], getRange([MILEAGE_RANGE_NAME])),
    mapPath(['outerColor'], getCheckboxNumbers([COLOR_NAME])),
    mapPath(['outerColorType'], getOuterColorType),
    mapPath(['price'], getRange([PRICE_RANGE_NAME])),
    mapPath(['monthlyPayment'], getRange([MONTHLY_PRICE_RANGE_NAME])),
    mapPath(['registrationDate'], getRange([YEAR_RANGE_NAME])),
    mapPath(['seatCount'], getRange([SEAT_RANGE_NAME])),
    mapPath(['sellerId'], get([SELLER_ID_NAME])),
    mapPath(['sellerType'], get([SELLER_TYPE_NAME])),
    mapPath(['exclusive'], getExclusive),
    mapPath(['upholstery'], getCheckboxNumbers([UPHOLSTERY_NAME])),
    mapPath(['serviceBook'], getCheckboxNumbers([SERVICE_HISTORY_NAME])),
    mapPath(
      ['size'],
      flow(getOr(DEFAULT_PAGE_SIZE, [PAGE_SIZE_NAME]), toNumber),
    ),
    mapPath(['smoker'], get([SMOKER_NAME])),
    mapPath(
      [HAS_IMAGES_NAME],
      flow(get([HAS_IMAGES_NAME]), (value) => value || undefined),
    ),
    mapPath(['sort'], getSort),
    mapPath(['fields'], getOr(AD_FIELDS, [FIELDS_NAME])),
    cleanEmptyValues,
    mapPath(['damageBool'], getDamagedBool),
    mapPath(['isReserved'], constant(false)), // This is added here intentionally after cleanEmptyValues as falsy value needs to be sent
  ],
  scaffolding,
);
